import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Folder as FolderIcon, Lock, Globe, AlertTriangle, Users, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import { FileTypeIcon } from '@/shared/ui/FileTypeIcon.jsx';
import { useGetRequest, usePatchRequest } from '@/shared/hooks/requests.js';
import { useInstallPolicies } from '@/shared/hooks/useInstallPolicies.js';
import { NODE, SHARE_USERS, SHARED_USERS_LIST } from '@/shared/utils/urls.js';
import { can, isLockedByMe, lockOf, versionOf } from '@/shared/utils/nodeAccess.js';
import { fileStateText } from '@/shared/utils/fileStateText.js';
import { formatDateTime } from '@/shared/utils/formatDate.js';
import { currentUserUuid } from '@/shared/auth/me.js';

// Read a value by camelCase key with a snake_case fallback (axios+humps
// camelCases responses, but some callers pass raw rows).
const pick = (obj, camel, snake) => obj?.[camel] ?? obj?.[snake];

const formatBytes = (bytes) => {
  if (bytes == null) return null;
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const n = bytes / Math.pow(1024, i);
  return `${n >= 10 || i === 0 ? Math.round(n) : n.toFixed(1)} ${units[i]}`;
};


const initials = (label) => (label || '?').trim().slice(0, 2).toUpperCase();

// Show a user as display name with their email beneath. Falls back to
// whichever of the two exists.
const UserText = ({ user }) => {
  const nm = user?.displayName || user?.display_name;
  const em = user?.email;
  if (nm && em) {
    return (
      <span className="flex flex-col leading-tight">
        <span className="text-foreground">{nm}</span>
        <span className="text-xs text-muted-foreground break-all">{em}</span>
      </span>
    );
  }
  return <span className="break-all">{nm || em || '—'}</span>;
};

const Row = ({ label, children }) => (
  <div className="flex items-baseline gap-4 py-[5px] text-sm">
    <span className="w-24 shrink-0 text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
    <span className="flex-1 min-w-0 text-foreground break-words">{children}</span>
  </div>
);

const Section = ({ children }) => (
  <div className="px-5 py-3 space-y-0.5">{children}</div>
);

// The folder owner's one access setting: whether access granted on the
// folders above reaches into this one. Off makes a private corner inside a
// shared folder (direct shares on it, signers and public visibility still
// apply). The install may not let owners turn it off.
const InheritSetting = ({ folder, t }) => {
  const policies = useInstallPolicies();
  const [inherit, setInherit] = useState(pick(folder, 'inheritShares', 'inherit_shares') !== false);
  const save = usePatchRequest({ url: NODE.replace('{node_uuid}', folder.uuid) });
  const breakAllowed = policies?.policies?.sharing?.allowInheritBreak !== false;
  const locked = save.loading || (inherit && !breakAllowed);

  const change = async (next) => {
    const { success } = await save.request({ data: { inheritShares: next } });
    if (!success) return;
    setInherit(next);
    toast.success(t('my-docs:properties.inheritSaved'));
  };

  return (
    <Section>
      <label className="flex items-start gap-2 text-sm">
        <Checkbox
          checked={inherit}
          disabled={locked}
          onCheckedChange={(c) => change(c === true)}
          aria-label={t('my-docs:properties.inherit')}
          data-testid="folder-inherit"
        />
        <span className="flex flex-col">
          <span className="text-foreground">{t('my-docs:properties.inherit')}</span>
          <span className="text-xs text-muted-foreground">
            {inherit && !breakAllowed ? t('my-docs:properties.inheritLocked') : t('my-docs:properties.inheritHint')}
          </span>
        </span>
      </label>
    </Section>
  );
};

/**
 * File-explorer style "Properties" panel for a file or folder. Shows core
 * metadata (type, owner, created/modified, location, size/items), the
 * visibility state including the stranded-public reachability warning, and
 * the users the resource is shared with. `resource`: the node's row (`uuid`,
 * `ownerUuid`…). `onManageSharing`, when provided, surfaces a shortcut to the
 * existing share modal.
 */
export const PropertiesModal = ({ open, setOpen, resource, type = 'file', onManageSharing }) => {
  const { t, i18n } = useTranslation(['my-docs', 'editor']);
  const isFolder = type === 'folder';
  const me = currentUserUuid();

  const sharedReq = useGetRequest({ url: SHARED_USERS_LIST });
  // The owner's name: a node read carries only `owner_uuid`, so the people
  // directory names them (unless it is me).
  const usersReq = useGetRequest({ url: SHARE_USERS });
  const [resolvedOwner, setResolvedOwner] = useState(null);

  const nodeUuid = resource?.uuid;
  const ownerUuid = pick(resource, 'ownerUuid', 'owner_uuid');
  const ownerIsMe = ownerUuid != null && me != null && String(ownerUuid) === String(me);

  useEffect(() => {
    if (!open || !nodeUuid) return;
    sharedReq.request({ params: { nodeUuid } });
    if (ownerUuid != null && !ownerIsMe) usersReq.request({ params: { limit: 500 } });
    setResolvedOwner(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, nodeUuid, type]);

  useEffect(() => {
    if (ownerIsMe) {
      setResolvedOwner({ self: true });
      return;
    }
    const list = usersReq.response || [];
    const match = list.find((u) => String(u.uuid) === String(ownerUuid));
    if (match) setResolvedOwner(match);
  }, [usersReq.response, ownerUuid, ownerIsMe]);

  if (!resource) return null;

  const name = isFolder
    ? resource.name
    : resource.name;
  const ext = (pick(resource, 'extension', 'extension') || '').toLowerCase();
  const visibility = resource.visibility || 'private';
  const reachable = resource.reachable;
  const isPublic = visibility === 'public';
  const stranded = isPublic && reachable === false;
  const size = pick(resource, 'size');
  const itemCount = pick(resource, 'itemCount', 'item_count');
  const created = formatDateTime(pick(resource, 'createdAt', 'created_at'), i18n.language);
  const modified = formatDateTime(pick(resource, 'updatedAt', 'updated_at'), i18n.language);
  const docType = pick(resource, 'approvalState', 'approval_state');
  // The current version (reads that carry it) and who holds the file.
  const version = isFolder ? null : versionOf(resource);
  const lock = isFolder ? null : lockOf(resource);
  const sharedWith = sharedReq.response || [];

  const kindLabel = isFolder
    ? t('my-docs:properties.folder')
    : ext
      ? t('my-docs:properties.fileOfType', { ext: ext.toUpperCase() })
      : t('my-docs:properties.file');

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg gap-0 p-0 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <DialogHeader className="space-y-0 border-b px-5 py-4 min-w-0">
          <DialogTitle asChild>
            <div className="flex items-center gap-3 min-w-0 pr-8">
              <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${isFolder ? 'bg-sky-50' : 'bg-muted'}`}>
                {isFolder
                  ? <FolderIcon className="w-6 h-6 text-sky-500" />
                  : <FileTypeIcon file={{ extension: ext }} className="w-6 h-6" />}
              </div>
              <div className="min-w-0">
                <h2 title={name} className="truncate text-base font-semibold leading-tight text-foreground">{name}</h2>
                <p className="truncate text-xs text-muted-foreground">{kindLabel}</p>
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="divide-y divide-border">
          {/* General */}
          <Section>
            {!isFolder && size != null && (
              <Row label={t('my-docs:properties.size') || 'Size'}>{formatBytes(size)}</Row>
            )}
            {isFolder && itemCount != null && (
              <Row label={t('my-docs:properties.items') || 'Items'}>{itemCount}</Row>
            )}
            <Row label={t('my-docs:properties.owner') || 'Owner'}>
              {resolvedOwner?.self
                ? (t('my-docs:properties.you') || 'You')
                : resolvedOwner
                  ? <UserText user={resolvedOwner} />
                  : '—'}
            </Row>
            {created && <Row label={t('my-docs:properties.created') || 'Created'}>{created}</Row>}
            {modified && <Row label={t('my-docs:properties.modified') || 'Modified'}>{modified}</Row>}
            {!isFolder && docType && docType !== 'ordinary' && (
              <Row label={t('my-docs:properties.state') || 'State'}>
                <span className="capitalize">{docType}</span>
              </Row>
            )}
            {version?.number != null && (
              <Row label={t('my-docs:properties.version')}>
                <span data-testid="props-version">{t('my-docs:properties.versionNumber', { n: version.number })}</span>
                {version.sealed && (
                  <span
                    className="ml-2 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
                    title={t('my-docs:properties.sealedHint')}
                  >
                    {t('my-docs:properties.sealed')}
                  </span>
                )}
              </Row>
            )}
            {lock && (
              <Row label={t('my-docs:properties.lock')}>
                {fileStateText(t, 'locked', lock, { mine: isLockedByMe(resource) })}
              </Row>
            )}
          </Section>

          {/* Visibility */}
          <Section>
            <Row label={t('my-docs:properties.visibility') || 'Visibility'}>
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${isPublic ? 'bg-sky-50 text-sky-700' : 'bg-rose-50 text-rose-700'}`}>
                {isPublic ? <Globe className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                {isPublic ? (t('my-docs:visibility.public') || 'Public') : (t('my-docs:visibility.private') || 'Private')}
              </span>
            </Row>
            {stranded && (
              <div className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-800">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{t('my-docs:properties.stranded')}</span>
              </div>
            )}
          </Section>

          {isFolder && can(resource, 'settings') && <InheritSetting folder={resource} t={t} />}

          {/* Shared with */}
          <Section>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <Users className="w-3.5 h-3.5" />
                {t('my-docs:properties.sharedWith') || 'Shared with'}
                {sharedWith.length > 0 && (
                  <span className="rounded-full bg-muted px-1.5 text-[10px] text-foreground/70">{sharedWith.length}</span>
                )}
              </span>
              {onManageSharing && (
                <button
                  type="button"
                  onClick={() => { setOpen(false); onManageSharing(); }}
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                >
                  <Share2 className="w-3 h-3" />
                  {t('my-docs:properties.manageSharing') || 'Manage'}
                </button>
              )}
            </div>
            {sharedWith.length > 0 ? (
              <ul className="mt-2 space-y-1.5">
                {sharedWith.map((u) => {
                  const nm = u.displayName || u.display_name;
                  return (
                    <li key={u.uuid} className="flex items-center gap-2 min-w-0">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold text-primary">
                        {initials(nm || u.email)}
                      </span>
                      <span className="flex flex-col min-w-0 leading-tight">
                        <span className="truncate text-sm text-foreground">{nm || u.email || '—'}</span>
                        {nm && u.email && (
                          <span className="truncate text-xs text-muted-foreground">{u.email}</span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="mt-1.5 text-sm text-muted-foreground">
                {t('my-docs:properties.notShared') || 'Not shared with anyone'}
              </p>
            )}
          </Section>
        </div>
      </DialogContent>
    </Dialog>
  );
};
